import React, { useState } from 'react';
import { Link, NavLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../context/CartContext';
import { useScrollPosition } from '../../hooks';
import { createPublicApi } from '../../services/api';

const Navbar = () => {
  const { slug } = useParams();
  const base = slug ? `/${slug}` : '';
  const { count, loadCartForSlug } = useCart();
  const [branding, setBranding] = React.useState({ navbar_name: '', navbar_logo_url: '' });

  React.useEffect(() => {
    if (slug) loadCartForSlug(slug);
  }, [slug]);

  // Fetch navbar branding from settings
  // Fetch navbar branding from settings
  React.useEffect(() => {
    if (!slug) return;
    const pubApi = createPublicApi(slug);
    const cacheKey = `cafe_navbar_${slug}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) setBranding(JSON.parse(cached));
    pubApi.settings.get()
      .then(r => {
        const s = r.data.data || {};
        const newBranding = { navbar_name: s.navbar_name || '', navbar_logo_url: s.navbar_logo_url || '' };
        setBranding(newBranding);
        sessionStorage.setItem(cacheKey, JSON.stringify(newBranding));
      })
      .catch(() => {});
  }, [slug]);

  const fullName = branding.navbar_name || '';
  const nameParts = fullName.trim().split(' ');
  const nameFirst = nameParts[0] || '';
  const nameRest = nameParts.slice(1).join(' ');
  const logoLetter = fullName.trim().charAt(0).toUpperCase() || 'A';

  const likesKey = `cafe_liked_items_${slug || 'default'}`;
  const [likedCount, setLikedCount] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem(`cafe_liked_items_${slug || 'default'}`) || '[]').length; }
    catch { return 0; }
  });

  React.useEffect(() => {
    try { setLikedCount(JSON.parse(localStorage.getItem(likesKey) || '[]').length); }
    catch { setLikedCount(0); }

    const sync = (e) => {
      if (e.detail?.slug && e.detail.slug !== slug) return;
      try { setLikedCount(JSON.parse(localStorage.getItem(likesKey) || '[]').length); }
      catch { setLikedCount(0); }
    };
    window.addEventListener('likes-updated', sync);
    return () => window.removeEventListener('likes-updated', sync);
  }, [slug, likesKey]);

  const location = useLocation();

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const scrollY = useScrollPosition();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const scrolled = scrollY > 20;

  const links = [
    { to: `${base}`, label: 'Home' },
    { to: `${base}/menu`, label: 'Menu' },
    { to: `${base}/order`, label: 'Order' },
    { to: `${base}/track-order`, label: 'Track Order' },
    { to: `${base}/reservation`, label: 'Reserve' },
    { to: `${base}/gallery`, label: 'Gallery' },
    { to: `${base}/about`, label: 'About' },
    { to: `${base}/contact`, label: 'Contact' },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled
        ? 'bg-white shadow-sm border-b border-cream-300'
        : 'bg-white'
    }`}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">

          {/* Logo */}
          <Link to={`${base}`} className="flex items-center gap-3 group">
            {branding.navbar_logo_url ? (
              <img
                src={branding.navbar_logo_url}
                alt="logo"
                className="w-9 h-9 rounded-xl object-cover shadow-sm group-hover:opacity-90 transition-opacity"
              />
            ) : (
              <img
                src="/favicon-512.png"
                alt="logo"
                className="w-9 h-9 rounded-xl object-cover shadow-sm group-hover:opacity-90 transition-opacity"
              />
            )}
            <span className="font-display text-xl font-semibold text-bark-900 tracking-wide">
              {nameFirst}{nameRest ? <> <span className="text-espresso-500">{nameRest}</span></> : ''}
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-0.5">
            {links.map(({ to, label }) => (
              <NavLink key={to} to={to} end={to === '/'}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 tracking-wide ${
                    isActive
                      ? 'text-espresso-600 bg-espresso-50'
                      : 'text-bark-600 hover:text-espresso-600 hover:bg-cream-100'
                  }`
                }
              >{label}</NavLink>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Favourites */}
            <button onClick={() => navigate(`${base}/menu`)}
              className="relative p-2 rounded-xl hover:bg-red-50 transition-colors"
              title="My Favourites">
              <svg className="w-5 h-5" fill={likedCount > 0 ? "#ef4444" : "none"} stroke={likedCount > 0 ? "#ef4444" : "currentColor"} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {likedCount > 0 && (
                <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold shadow-sm">
                  {likedCount}
                </motion.span>
              )}
            </button>

            {/* Cart */}
            <button onClick={() => navigate(`${base}/order`)}
              className="relative p-2 rounded-xl hover:bg-cream-100 transition-colors">
              <svg className="w-5 h-5 text-bark-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {count > 0 && (
                <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 bg-espresso-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold shadow-sm">
                  {count}
                </motion.span>
              )}
            </button>

            {/* Mobile menu */}
            <button onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-xl hover:bg-cream-100 transition-colors">
              <div className="w-5 flex flex-col gap-1.5">
                <span className={`block h-0.5 bg-bark-700 rounded transition-all ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
                <span className={`block h-0.5 bg-bark-700 rounded transition-all ${mobileOpen ? 'opacity-0' : ''}`} />
                <span className={`block h-0.5 bg-bark-700 rounded transition-all ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-b border-cream-300">
            <div className="px-4 py-4 flex flex-col gap-1">
              {links.map(({ to, label }) => (
                <NavLink key={to} to={to} end={to === '/'} onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl text-sm font-medium tracking-wide transition-all ${
                      isActive
                        ? 'text-espresso-600 bg-espresso-50'
                        : 'text-bark-700 hover:bg-cream-100'
                    }`
                  }
                >{label}</NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
